import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { ArrowRight, Lock, User, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.password) {
      toast.error("Please enter both username and password.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, {
        username: form.username.trim(),
        password: form.password,
      });
      localStorage.setItem("saksham_cms_token", data.token);
      localStorage.setItem("saksham_cms_user", data.username);
      toast.success("Welcome back.");
      navigate("/cms");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Invalid credentials. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-login" className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6 pt-32 pb-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#FBD223] mb-6">
            <Lock className="text-[#0A0A0A]" size={20} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#09B792] font-semibold mb-3">Admin Login</p>
          <h1 className="font-serif text-4xl md:text-5xl font-medium leading-tight tracking-tight">
            Welcome back.
          </h1>
          <p className="mt-3 text-sm text-[#4A4A4A]">Sign in to manage content.</p>
        </div>

        <form onSubmit={submit} data-testid="login-form" className="bg-white border border-black/5 p-8 md:p-10 space-y-6" noValidate>
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-[#4A4A4A] font-semibold flex items-center gap-2">
              <User size={14} />
              Username
            </label>
            <input
              type="text"
              data-testid="login-input-username"
              value={form.username}
              onChange={set("username")}
              placeholder="Enter username"
              autoComplete="username"
              className="mt-1 w-full bg-transparent border-b border-black/20 focus:border-[#09B792] outline-none py-3 text-base"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-[#4A4A4A] font-semibold flex items-center gap-2">
              <Lock size={14} />
              Password
            </label>
            <div className="mt-1 flex items-center border-b border-black/20 focus-within:border-[#09B792]">
              <input
                type={showPwd ? "text" : "password"}
                data-testid="login-input-password"
                value={form.password}
                onChange={set("password")}
                placeholder="Enter password"
                autoComplete="current-password"
                className="flex-1 bg-transparent outline-none py-3 text-base"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                data-testid="login-toggle-password"
                aria-label={showPwd ? "Hide password" : "Show password"}
                className="p-2 text-[#4A4A4A] hover:text-[#0A0A0A]"
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-[#FBD223] hover:bg-[#FBD223] hover:text-[#0A0A0A] disabled:opacity-50 disabled:cursor-not-allowed px-8 py-4 text-sm font-medium transition-colors"
          >
            {submitting ? "Signing in…" : "Sign In"} <ArrowRight size={16} />
          </button>
        </form>

        <div className="text-center mt-6">
          <Link to="/" className="text-xs text-[#4A4A4A] hover:text-[#0A0A0A] link-underline">← Back to site</Link>
        </div>
      </motion.div>
    </div>
  );
}
